import React from "react";
import { connect } from "react-redux";
import { Field, FieldArray } from "formik";
import { debounce } from "lodash";
import moment from "moment";
import swal from "sweetalert";
import * as Toastr from "toastr";

import { swalDefaultConfig } from "../../utils";
import {
  destroy,
  destroyAll,
  update,
} from "../redux/actions/scheduleItemsAction";
import CheckBox from "../../../DataEntry/CheckBox";
import InputNumber from "../../../DataEntry/InputNumber";
import Select from "../../../DataEntry/Select";
import PopConfirm from "../../../modal/PopConfirm";

class Table extends React.Component<any, any> {
  componentDidUpdate(prevProps: any, _prevState: any) {
    const { scheduleItems, formik } = this.props;
    if (scheduleItems.length !== prevProps.scheduleItems.length) {
      const reconciledData = [];
      scheduleItems.forEach((elm: any, idx: number) => {
        reconciledData[idx] = formik.values.items.find(
          (c: any) => c.id === elm.id,
        ) || { ...elm, checked: false };
      });
      formik.setFieldValue("all", false);
      formik.setFieldValue("items", reconciledData);
    }
  }

  handleToggleAll = (event: any) => {
    const { formik, scheduleItems } = this.props;
    const checked = event.target.checked;
    const { values } = formik;
    const { items } = values;

    scheduleItems.forEach((_elm: any, idx: number) => {
      items[idx] = {
        ...items[idx],
        checked: checked && items[idx].shipped_qty === "",
      };
    });

    formik.setValues({ ...values, items, all: checked }, false);
  };

  handleBlurScheduleItem = debounce((idx: number) => {
    const { formik, orderId, scheduleItems } = this.props;
    const row = formik.values.items[idx];
    if (row.ordered_qty == scheduleItems[idx].ordered_qty) return;

    const { update } = this.props;
    update(orderId, row.id, row).catch((error: any) => {
      Toastr.error(error.message);
      formik.setFieldValue(
        `items.${idx}.ordered_qty`,
        scheduleItems[idx].ordered_qty,
      );
    });
  }, 500);

  handleDestroy = (idx: number) => {
    const { destroy, orderId } = this.props;
    destroy(orderId, idx);
  };

  handleBulkAction = ({ target }: any) => {
    const { formik } = this.props;
    switch (target.value) {
      case "delete":
        const checked = formik.values.items.filter((i: any) => i.checked);
        if (!checked.length) {
          Toastr.warning("Please select an item at least");
          break;
        }

        const { destroyAll, orderId } = this.props;
        swal({
          ...swalDefaultConfig,
          text: "This will delete selected schedule order(s)",
        }).then((willConfirm: boolean) => {
          if (willConfirm) {
            destroyAll(orderId, { id: checked.map((i: any) => i.id) });
          } else {
            return false;
          }
        });
        break;
    }

    formik.setFieldValue("action", "");
  };

  handleCheckItem = ({ target }: any) => {
    const { formik } = this.props;
    if (target.checked) {
      const index = parseInt(target.name.match(/\d+/)[0]);
      const checked = formik.values.items
        .filter((i: any) => i.shipped_qty === "")
        .every((i: any, idx: number) => idx === index || i.checked);

      formik.setFieldValue("all", checked);
    } else {
      formik.setFieldValue("all", false);
    }
  };

  renderStatus = (status: number) => {
    switch (status) {
      case 0:
        return <span className="text-warning">Pending</span>;
      case 1:
        return <span className="text-primary">Firmed</span>;
      case 2:
        return <span className="text-success">Completed</span>;

      default:
        return;
    }
  };

  render() {
    const {
      clientCode,
      deleteAllowed,
      editAllowed,
      orderId,
      scheduleItems,
      storeCode,
      viewOnly,
    } = this.props;

    return (
      <table className="table table-hover table-bordered table-responsive smart-table">
        <thead className="dark-green-header">
          <tr className="center-content">
            <th style={{ width: "2%" }}>
              <Field
                name="all"
                component={CheckBox}
                onChange={this.handleToggleAll}
                disabled={viewOnly || !deleteAllowed}
              />
            </th>
            <th>
              Schedule Order <br /> Invoice #
            </th>
            <th className="text-left">Item SKU / Name</th>
            <th>
              Expected <br /> Ship Date
            </th>
            <th>
              Scheduled <br /> Pickup Date
            </th>
            <th>
              Shipped <br /> Date
            </th>
            <th>Shipped From</th>
            <th>Ship To</th>
            <th style={{ width: "5%" }}>
              Ordered <br /> QTY
            </th>
            <th style={{ width: "5%" }}>
              Shipped <br /> QTY
            </th>
            <th style={{ width: "5%" }}>
              Return <br /> QTY
            </th>
            <th>Status</th>
            <th style={{ width: "3%" }}></th>
          </tr>
        </thead>
        <tbody>
          <FieldArray
            name="items"
            render={() =>
              scheduleItems.map((si: any, idx: number) => {
                const disabled = viewOnly || si.shipped_qty !== "";

                return (
                  <tr
                    key={si.id}
                    className="center-content"
                    style={disabled ? { backgroundColor: "#F5F5F5" } : {}}
                  >
                    <td>
                      <Field
                        name={`items.${idx}.checked`}
                        component={CheckBox}
                        onChange={this.handleCheckItem}
                        disabled={disabled || !deleteAllowed}
                      />
                    </td>
                    <td>{si.schedule_number}</td>
                    <td className="text-left without-control">
                      {si.description} SKU: {si.sku}
                    </td>
                    <td className="without-control">
                      {si.expected_ship_date &&
                        moment(si.expected_ship_date).format("MM/DD/YYYY")}
                    </td>
                    <td className="without-control">
                      {si.scheduled_ship_date &&
                        moment(si.scheduled_ship_date).format("MM/DD/YYYY")}
                    </td>
                    <td className="without-control">
                      {si.actual_shipped_date &&
                        moment(si.actual_shipped_date).format("MM/DD/YYYY")}
                    </td>
                    <td className="text-left without-control">{`${si.ship_from} (${si.code})`}</td>
                    <td className="text-left without-control">{si.ship_to}</td>
                    <td>
                      <Field
                        name={`items.${idx}.ordered_qty`}
                        className={`smart-form-control ${disabled ? "smart-control-readonly" : ""} text-center`}
                        component={InputNumber}
                        onBlur={() => this.handleBlurScheduleItem(idx)}
                        disabled={disabled || !editAllowed}
                      />
                    </td>
                    <td>{si.shipped_qty}</td>
                    <td>{si.returned_qty}</td>
                    <td>{this.renderStatus(si.status)}</td>
                    <td className="sn-icons">
                      {disabled || !deleteAllowed ? (
                        <a className="fa fa-minus-circle disabled" />
                      ) : (
                        <PopConfirm
                          text="This will delete schedule order information."
                          onConfirm={() => this.handleDestroy(si.id)}
                        >
                          <a className="fa fa-minus-circle delete-icon pointer" />
                        </PopConfirm>
                      )}
                      <a
                        className={`fa fa-edit edit-icon${editAllowed ? "" : " disabled"}`}
                        href={
                          editAllowed &&
                          `/${clientCode}/${storeCode}/crm/schedules/${si.schedule_id}/edit?order_id=${orderId}&order=true`
                        }
                      />
                    </td>
                  </tr>
                );
              })
            }
          />
          <tr>
            <td colSpan={8}>
              <Field
                name="action"
                className="smart-form-control"
                component={Select}
                list={[
                  ["", "Select Bulk Action"],
                  ["delete", "Delete"],
                ]}
                style={{ width: "20%" }}
                onChange={this.handleBulkAction}
                disabled={viewOnly || !deleteAllowed}
              />
            </td>
            <td className="text-center bold">
              {scheduleItems
                .reduce(
                  (total: any, { ordered_qty }: any) =>
                    (total += parseInt(ordered_qty || 0)),
                  0,
                )
                .toLocaleString()}
            </td>
            <td className="text-center bold">
              {scheduleItems
                .reduce(
                  (total: any, { shipped_qty }: any) =>
                    (total += parseInt(shipped_qty || 0)),
                  0,
                )
                .toLocaleString()}
            </td>
            <td className="text-center bold">
              {scheduleItems
                .reduce(
                  (total: any, { return_qty }: any) =>
                    (total += parseInt(return_qty || 0)),
                  0,
                )
                .toLocaleString()}
            </td>
            <td colSpan={2}></td>
          </tr>
        </tbody>
      </table>
    );
  }
}

const mapStateToProps = (state: any, _props: any) => ({
  deleteAllowed: state.permissions.data.order_delete_schedule_order,
  editAllowed: state.permissions.data.order_edit_schedule_order,
  scheduleItems: state.scheduleItems.data,
});

const mapDispatchToProps = { destroy, destroyAll, update };

export default connect(mapStateToProps, mapDispatchToProps)(Table);
